import { makeStyles } from '@material-ui/core/styles';
import mixins from '../../assets/styles/mixins.scss';

/** Date + time columns (144px each) + row gaps (8px + 8px); must match dateTimeField layout */
const DATE_TIME_ROW_RESERVED_PX = 304;

const useAssignmentStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: 16,
    backgroundColor: '#f8f9fa',
    height: 525
  },
  assignmentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  },
  title: {
    fontSize: 20,
    fontWeight: mixins.fontWeight500,
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    marginBottom: 8
  },
  dialogTitleRoot: {
    padding: '0px !important'
  },
  addButton: {
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    padding: '12px 24px',
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    '&:hover': {
      backgroundColor: mixins.amethyst,
      opacity: 0.9
    }
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: '12px',
    border: '2px dashed #e9ecef',
    textAlign: 'center'
  },
  emptyAction: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '20px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f8f9fa'
    },
    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 0 3px rgba(0,123,255,0.12)'
    }
  },
  emptyActionIcon: {
    fontSize: '48px',
    color: mixins.amethyst
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: 600,
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans
  },
  assignmentItem: {
    overflow: 'hidden',
    boxSizing: 'border-box',
    minHeight: 70,
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 8,
    padding: 8,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    transition: 'all 0.2s ease',
    justifyContent: 'space-between',
    '&:hover': {
      border: `1px solid ${mixins.amethyst}`,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      transform: 'translateY(-2px)',
      '& .MuiIconButton-root': { // ADD: Show edit button on hover
        opacity: 1,
        visibility: 'visible'
      }
    }
  },
  editButton: {
    color: mixins.blackColor,
    padding: '8px',
    visibility: 'hidden',
    '&:hover': {
      color: mixins.color_text_primary
    }
  },
  verticalDivider: {
    width: '1.5px',
    height: '40px',
    backgroundColor: '#D3D1E2',
    margin: '0 8px'
  },
  dateSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 56,
    borderRadius: '8px'
  },
  dateNumber: {
    fontSize: 18,
    fontWeight: mixins.fontWeight600,
    color: mixins.color_text_primary,
    lineHeight: 1,
    fontFamily: mixins.fontFamilyPlusSans
  },
  dateMonth: {
    fontSize: 12,
    fontWeight: mixins.fontWeight600,
    color: mixins.color_text_primary,
    letterSpacing: '0.5px',
    fontFamily: mixins.fontFamilyPlusSans
  },
  contentSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  assignmentTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: 1.4
  },
  chapterTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  chapterChip: {
    backgroundColor: mixins.quaternary,
    color: mixins.color_text_primary,
    borderRadius: 4,
    fontWeight: 500,
    minHeight: 24,
    height: 'auto',
    '& .MuiChip-label': {
      padding: '0 8px',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: 12,
      fontWeight: mixins.fontWeight500,
      whiteSpace: 'normal'
    }
  },
  dialogContent: {
    minWidth: 400,
    padding: '0 !important',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    height: 525,

    '&&': {
      overflowY: 'hidden'
    }
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: '100%'
  },
  addEditBtn: {
    minWidth: 176,
    width: 'auto',
    height: 40,
    boxSizing: 'border-box',
    backgroundColor: mixins.amethyst,
    padding: '8px 24px',
    gap: 16,
    borderRadius: 40,
    '&:hover': {
      backgroundColor: mixins.amethyst
    }
  },
  // Modern form styles
  modernFormContainer: {
    borderRadius: '12px',
    backgroundColor: mixins.chalkWhiteFont,
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  modalWrapper: {
    padding: '48px !important'
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  dateTimeSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: 'var(--form-fields-max-width)',
    alignSelf: 'flex-start'
  },
  dateTimeSectionModal: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    flexWrap: 'wrap',
    width: 'var(--form-fields-max-width)',
    alignSelf: 'flex-start'
  },
  dateTimeRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    width: '100%',
    '--timezone-field-width': `max(200px, calc(var(--form-fields-max-width, 520px) - ${DATE_TIME_ROW_RESERVED_PX}px))`
  },
  assignmentTitleField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
    width: '100%'
  },
  dateTimeField: {
    flex: '0 1 auto',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 144,
    gap: 8
  },
  dateTimeFieldTimezone: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: 'var(--timezone-field-width)',
    minWidth: 200,
    maxWidth: 'var(--timezone-field-width)',
    boxSizing: 'border-box'
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: mixins.fontWeight400,
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '22px'
  },
  titleField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '& fieldset': {
        borderColor: '#e0e0e0'
      },
      '&:hover fieldset': {
        borderColor: '#b0b0b0'
      },
      '&.Mui-focused fieldset': {
        borderColor: mixins.amethyst
      }
    }
  },
  dateField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '& fieldset': {
        borderColor: '#e0e0e0'
      },
      '&:hover fieldset': {
        borderColor: '#b0b0b0'
      },
      '&.Mui-focused fieldset': {
        borderColor: mixins.amethyst
      }
    }
  },
  timeField: {
    '& .MuiOutlinedInput-root': {
      cursor: 'pointer',
      width: 144,
      height: 48,
      boxSizing: 'border-box',
      borderRadius: 4,
      padding: 12,
      backgroundColor: '#FFFFFF',
      border: `1px solid ${mixins.amethyst}`,
      display: 'flex',
      justifyContent: 'center',
      '& input': {
        padding: '0px !important',
        '&::-webkit-calendar-picker-indicator': {
          display: 'none',
          opacity: 0,
          pointerEvents: 'none'
        },
        '&::-webkit-inner-spin-button': {
          display: 'none'
        },
        '&::-webkit-outer-spin-button': {
          display: 'none'
        }
      },
      '& fieldset': {
        border: 'none !important'
      }
    }
  },
  timeFieldWrapper: {
    position: 'relative',
    display: 'inline-block',
    width: '100%'
  },
  timeIcon: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: mixins.timeIconColor,
    fontSize: '20px'
  },
  chapterSection: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0.6)',
    zIndex: 10,
    pointerEvents: 'all',
    cursor: 'not-allowed'
  },
  chapterHeader: {
    fontSize: '16px',
    fontWeight: 600,
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    marginTop: 12
  },
  actionSection: {
    display: 'flex',
    paddingTop: 16,
    justifyContent: 'flex-end',
    flexDirection: 'column',
    gap: 12
  },
  summaryInfo: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8
  },
  summaryText: {
    fontSize: 12,
    fontWeight: 500,
    color: mixins.amethyst,
    lineHeight: '18px',
    fontFamily: mixins.fontFamilyPlusSans,
    borderRadius: 4,
    background: mixins.badgeBgColor,
    padding: '0px 6px 2px 6px'
  },
  summaryQuestion: {
    fontSize: 12,
    fontWeight: 500,
    color: mixins.amethyst,
    lineHeight: '18px',
    fontFamily: mixins.fontFamilyPlusSans,
    borderRadius: 4,
    background: mixins.badgeBgColor,
    padding: '0px 6px 2px 6px'
  },
  assignmentsCount: {
    fontSize: 12,
    fontWeight: 500,
    color: mixins.amethyst,
    lineHeight: '20px',
    fontFamily: mixins.fontFamilyPlusSans,
    borderRadius: 4,
    background: mixins.badgeBgColor,
    padding: '0px 4px'
  },
  buttonGroup: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  saveAndAddButton: {
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '22px',
    padding: 8,
    borderRadius: 1000,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: 14,
    minWidth: 193,
    maxHeight: 40,

    '&:hover': {
      backgroundColor: mixins.amethyst,
      opacity: 0.9
    },
    '&:disabled': {
      backgroundColor: '#e0e0e0',
      color: '#9e9e9e'
    }
  },
  saveAndCloseButton: {
    backgroundColor: mixins.chalkWhiteFont,
    color: mixins.navyColor,
    border: `2px solid ${mixins.navyColor}`,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '22px',
    padding: 8,
    borderRadius: 1000,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: 14,
    minWidth: 132,
    maxHeight: 40,

    '&:hover': {
      backgroundColor: '#f8f5ff',
      borderColor: mixins.amethyst
    },
    '&:disabled': {
      backgroundColor: '#f5f5f5',
      color: '#9e9e9e',
      borderColor: '#e0e0e0'
    }
  },
  clodeIcon: {
    position: 'absolute',
    right: 16,
    top: 16,
    cursor: 'pointer',
    padding: '8px',
    height: 18,
    width: 18,
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: mixins.quaternary,
      borderRadius: '50%'
    }
  },
  addEditDialogPaper: {
    maxHeight: '980px !important',
    height: 'stretch !important',
    overflowY: 'hidden !important',
    minWidth: '704 px !important',
    maxWidth: '800px !important',
    padding: 48,
    borderRadius: 24,
    marginBottom: '17px !important',
    marginTop: '17px !important'
  },
  summaryInfoWrapper: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between'
  },
  assignmentsListHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 16,
    marginTop: 6
  },
  customCheckbox: {
    color: mixins.amethyst,
    '&.Mui-checked': {
      color: mixins.amethyst
    },
    '&.MuiCheckbox-indeterminate': {
      color: mixins.amethyst
    },
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  assignmentsListHeaderTitle: {
    fontSize: 16,
    fontWeight: mixins.fontWeight500,
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    marginLeft: 4
  },
  assignmentHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  leftAlign: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8
  },
  deleteAllBtn: {
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  // DashboardCard styles for assignment list
  dashboardCardRoot: {
    backgroundColor: '#EFEEF7',
    maxWidth: '1280px',
    margin: '40px auto',
    '--custom-card-list-max-height': '500px'
  },
  assignmentsTabularContainer: {
    padding: 16,
    backgroundColor: '#fff',
    borderRadius: '16px',
    '--table-container-head-padding-top': '0px',
    '--table-container-head-padding-left': '8px',
    '--table-container-head-padding-right': '8px',
    '--table-container-cell-padding-top': '8px',
    '--table-container-cell-padding-bottom': '8px',
    '--table-container-cell-padding-left': '8px',
    '--table-container-cell-padding-right': '8px',
    '--table-container-cell-gap': '4px'
  },
  // Table-specific styles (mirroring TabularStyles)
  paddingCheckbox: {
    padding: '0px',
    color: `${mixins.amethyst} !important`,
    '&.Mui-disabled': {
      color: `${mixins.greyScaleColor} !important`,
      opacity: 0.5
    }
  },
  tableContainer: {
    backgroundColor: '#f8f9fa',
    padding: 16,
    borderRadius: 8
  },
  tableWrapper: {
    maxHeight: 500,
    overflow: 'auto',
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 8
  },
  table: {
    minWidth: 650,
    '--table-head-padding-top': '0px',
    '--table-head-padding-left': '0px',
    '--table-head-padding-right': '0px',
    '--table-head-padding-bottom': '12px',
    '--table-cell-padding-top': '12px',
    '--table-cell-padding-bottom': '12px',
    '--table-cell-padding-left': '0px',
    '--table-cell-padding-right': '0px',
    '--table-cell-gap': '12px',
    '& .MuiTableCell-stickyHeader': {
      backgroundColor: `${mixins.chalkWhiteFont} !important`,
      position: 'sticky'
    }
  },
  tableHeadRow: {
    '& th': {
      backgroundColor: 'unset',
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize12,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight500,
      lineHeight: '166.667%',
      padding: '0px',
      paddingBottom: 'var(--table-head-padding-bottom)',
      paddingTop: 'var(--table-head-padding-top)',
      paddingLeft: 'var(--table-head-padding-left)',
      paddingRight: 'var(--table-head-padding-right)'
    }
  },
  tableHeadCell: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    color: mixins.color_text_primary,
    lineHeight: '166.667%',
    paddingTop: 'var(--table-head-padding-top)',
    paddingBottom: 'var(--table-head-padding-bottom)',
    paddingLeft: 'var(--table-head-padding-left)',
    paddingRight: 'var(--table-head-padding-right)',
    position: 'relative',
    borderBottom: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 'var(--table-head-padding-top)',
      right: 0,
      width: '1px',
      height: 'calc(100% - var(--table-head-padding-top) - var(--table-head-padding-bottom))',
      backgroundColor: 'rgba(13, 0, 77, 0.15)'
    },
    '&:first-child': {
      '& > $tableCellContent': {
        paddingLeft: 0
      }
    },
    '& > $tableCellContent': {
      paddingLeft: 'var(--table-cell-gap)',
      paddingRight: 'var(--table-cell-gap)'
    },
    '&:last-child': {
      '& > $tableCellContent': {
        paddingRight: 0
      },
      '&::after': {
        content: 'none'
      }
    }
  },
  tableBodyRow: {
    '&:hover': {
      backgroundColor: 'rgba(13, 0, 77, 0.15)',
      cursor: 'pointer',
      '& .editActionIcon': {
        visibility: 'visible'
      }
    },
    '&:last-child': {
      '& $tableCell': {
        borderBottom: 'none'
      }
    }
  },
  tableCell: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 14,
    color: mixins.color_text_primary,
    paddingTop: 'var(--table-cell-padding-top)',
    paddingBottom: 'var(--table-cell-padding-bottom)',
    paddingLeft: 'var(--table-cell-padding-left)',
    paddingRight: 'var(--table-cell-padding-right)',
    borderBottom: '1px solid rgba(13, 0, 77, 0.15)',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 'var(--table-cell-padding-top)',
      right: 0,
      width: '1px',
      height: 'calc(100% - var(--table-cell-padding-top) - var(--table-cell-padding-bottom))',
      backgroundColor: 'rgba(13, 0, 77, 0.15)'
    },
    '&:first-child': {
      '& > $tableCellContent': {
        paddingLeft: 0
      }
    },
    '& > $tableCellContent': {
      paddingLeft: 'var(--table-cell-gap)',
      paddingRight: 'var(--table-cell-gap)'
    },
    '&:last-child': {
      '& > $tableCellContent': {
        paddingRight: 0
      },
      '&::after': {
        content: 'none'
      }
    }
  },
  tableCellContent: {
    display: 'flex',
    alignItems: 'flex-start'
  },
  stickyHeader: {
    backgroundColor: `${mixins.chalkWhiteFont} !important`,
    position: 'sticky'
  },
  assignmentColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  titleWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  editActionIconButton: {
    padding: 0,
    minWidth: 0,
    margin: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    textTransform: 'none',
    '&:hover': {
      background: 'none'
    },
    '&:focus': {
      background: 'none'
    },
    '&:active': {
      background: 'none'
    },
    '&.editActionIcon': {
      visibility: 'hidden'
    }
  },
  sectionsRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
    overflowX: 'auto',
    marginTop: 2
  },
  sectionsLabel: {
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    display: 'flex',
    alignItems: 'center',
    lineHeight: '20px'
  },
  sectionChip: {
    background: mixins.feedBackColor,
    borderRadius: 4,
    padding: '0 2px',
    display: 'flex',
    alignItems: 'center',
    marginRight: 2
  },
  chipContent: {
    padding: '0 4px 2px 4px',
    height: 20,
    gap: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  sectionNumber: {
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    lineHeight: '20px',
    fontWeight: mixins.fontWeight500,
    display: 'flex',
    alignItems: 'center'
  },
  sectionItem: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight400,
    color: mixins.color_text_primary,
    lineHeight: '166.667%',
    '&:not(:last-child)::after': {
      content: '""',
      marginLeft: 4
    }
  },
  dueDateText: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 14,
    fontWeight: mixins.fontWeight400,
    color: mixins.color_text_primary,
    lineHeight: '157.143%'
  },
  dateCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2
  },
  dateCellDay: {
    fontSize: 16,
    fontWeight: mixins.fontWeight600,
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: 1.2
  },
  dateCellMonth: {
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    color: mixins.zambezi,
    fontFamily: mixins.fontFamilyPlusSans,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  footer: {
    color: mixins.zambezi,
    fontWeight: mixins.fontWeight500,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    lineHeight: '20px',
    display: 'flex',
    alignItems: 'center'
  },
  errorBorder: {
    border: `2px solid ${mixins.accentRedDark}`,
    borderRadius: '8px',
    padding: '8px',
    boxSizing: 'border-box'
  },
  dateTemplateWrapper: {
    width: '100%',

    '& > div': {
      justifyContent: 'flex-end',
      paddingRight: 8
    }
  },
  assignmentListTable: {
    tableLayout: 'fixed',
    width: '100%'
  },
  assignmentCompletionColumn: {
    paddingLeft: '8px !important',
    paddingRight: '8px !important'
  },
  assignmentStatusCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8
  },
  assignmentStatusInfoIcon: {
    fontSize: 20,
    color: mixins.color_text_primary,
    flexShrink: 0,
    marginTop: 1
  },
  assignmentStatusWarningIcon: {
    fontSize: 20,
    color: '#B45309',
    flexShrink: 0,
    marginTop: 1
  },
  assignmentStatusText: {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    lineHeight: '20px',
    fontWeight: mixins.fontWeight400
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    borderRadius: 8,
    width: '100%',
    height: '100%',
    margin: '0 auto',
    position: 'absolute',
    backgroundColor: mixins.chalkWhiteFont
  },
  loadingProgress: {
    color: mixins.amethyst
  }
}));

export default useAssignmentStyles;
